import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Highlight, { defaultProps } from 'prism-react-renderer'

import Prism from 'prism-react-renderer/prism'

import styled, { css } from 'styled-components'
import copy from 'clipboard-copy'
import rangeParser from 'parse-numeric-range'
import typography from '../../theme/typography'

import Text from 'components/Text'

import lightTheme from './lightTheme.js'
import darkTheme from './darkThemeNew.js'
import useAllSdkVersionsQuery from '../../hooks/useAllSdkVersionsQuery'
;(typeof global !== 'undefined' ? global : window).Prism = Prism

require('prismjs/components/prism-kotlin')
require('prismjs/components/prism-java')
require('prismjs/components/prism-swift')
require('prismjs/components/prism-ruby')
require('prismjs/components/prism-typescript')

// prettier-ignore
const Wrapper = styled.div`
	position: relative;
	overflow: hidden;
	border-radius: 0.5rem;
	background: var(--color-alabaster);

	[data-theme='light'] & {
		${lightTheme};
		background: var(--color-alabaster) !important;
	}
	[data-theme='dark'] & {
		${darkTheme};
		background: var(--color-shark) !important;
	}

	tab-size: 2;
`

// prettier-ignore
const LanguageTag = styled.div`
	${typography.scale.code.regular}
	text-transform: uppercase;
	position: absolute;
	top: 0;
	left: 0;
	background: var(--color-pattensBlue);
	color: var(--color-black);
	pointer-events: null;
	border-radius: 0 0 0.25rem 0;
	user-select: none;
	padding: 8px 16px;
`

// prettier-ignore
const CopyButton = styled.div`
	${typography.scale.code.regular}
	position: absolute;
	top: 0;
	right: 16px;
	padding: 8px 16px;
	background: var(--color-lighterGrey);
	color: var(--color-black);
	transform: translateY(-100%);

	pointer-events: null;
	border-radius: 0 0 0.25rem 0.25rem;
	cursor: pointer;

	transition: transform 200ms ease;
	&:focus {
		background: var(--color-warmGray);
		opacity: 1;
		pointer-events: all;
	}
	${p => p.visible && css`
		transform: translateY(0%);
		pointer-events: all;
	`}

	/* ${Wrapper}:hover &, ${Wrapper}:focus-within & {
		opacity: 1;
		pointer-events: all;
	} */
	[data-copied='true'] {
		background: var(--color-black);
		color: var(--color-white);
		&:focus {
			background: var(--color-black) !important;
			color: var(--color-white) !important;
			box-shadow: 0 0 0 1px var(--color-black);
		}
	}
`

const CodeStyle = styled(Text).attrs({ as: 'pre', xs: 1, family: 'mono' })``

// prettier-ignore
const Outer = styled.div`
	position: relative;
	padding: 1rem 0;
	${p => p.showLanguageTag && css`
		padding-top: 2.5rem !important;
	`}
`

// prettier-ignore
const CodeHolder = styled.div`
	margin: 0 !important;
	padding: 0 !important;
	max-width: 100%;
	user-focusable: none;
	background: none !important;
	display: flex;
	position: relative;

	& *::selection {
		color: inherit;
		background: #00000010;
		[data-theme='dark'] & {
			background: #FFFFFF30;
		}
	}
`

const lineNumberMinWidthCh = 6
const lineNumberPaddingCh = 2

const ScrollArea = styled(CodeStyle)`
	position: relative;
	overflow-x: auto;
	overflow-y: hidden;
	margin-bottom: -1rem;
	padding-bottom: 1rem;
	${p =>
		p.lineNumbers &&
		css`
			padding-left: ${Math.max(lineNumberMinWidthCh, `${p.totalLines}`.length + 2 * lineNumberPaddingCh)}ch;
		`}
`

// prettier-ignore
const LineNumber = styled.span`
	${typography.scale.code.regular}

	box-sizing: border-box;
	display: block;
	position: sticky;
	color: var(--color-warmGray);
	left: 0;
	min-width: ${lineNumberMinWidthCh}ch;
	padding: 0 ${lineNumberPaddingCh}ch;
	user-select: none;
	text-align: right;
	z-index: 1;
	background: var(--color-alabaster);
	[data-theme='dark'] & {
		background: var(--color-shark);
		color: #747484;
	}

	${p => !!p.highlightBackground && css`
		transition: 0.5s background;
		background: var(--color-whiteIce);
		[data-theme='dark'] & {
			background: #18191a;
		}
	`}
`

// prettier-ignore
const Line = styled.span`
	${typography.scale.code.regular}

	display: block;

	.token {
		color: #75758C;
		transition: color 0.2s ease-in-out;

		[data-theme='dark'] & {
			color: #D9E6FF;
		}
	}

	${p => !!p.faded && css`
		.token {
			${Wrapper}:not(:hover) & {
				[data-theme='dark'] & {
					color: #747484;
				}
			}
		}
	`}

	&:after {
		content: '';
		padding-right: 2ch;
	}

	/* Fix weird case where empty lines take 2 lines */
	& .token.plain:only-child {
		display: none;
	}

`

const defaultOptions = {
	lineNumbers: true,
	copyToClipboard: true,
	showLanguageTag: true,
}

const CollapseButton = styled(Text).attrs({ as: 'div', xs: 2 })`
	text-align: center;
	display: block;
	padding: 0.25rem;
	cursor: pointer;
	background: var(--color-pattensBlue);
	color: var(--color-black);
	width: 100%;
`

const LinesBackground = styled(CodeStyle)`
	position: absolute;
	z-index: 0;
	overflow: hidden;
	padding: 0 !important;
	margin: 0 !important;
	line-height: 20px !important;
	color: transparent !important;
	pointer-events: none;
	background: var(--color-alabaster) !important;
	width: 100%;
	left: 0;
	right: -1rem;
	& > * {
		width: calc(100% + 2ch);
		&:after {
			content: '';
			padding-right: 2ch;
		}
	}
	[data-theme='dark'] & {
		background: var(--color-shark) !important;
	}
	& [data-highlight='true'] {
		transition: 0.5s background;
		background: var(--color-whiteIce);
		[data-theme='dark'] & {
			background: #18191a;
		}
	}
`

const LineNumbers = styled(CodeStyle)`
	display: block;
	position: absolute;
	left: 0;
	& * {
		display: block;
	}
`

const CodeBlock = ({ language = 'jsx', code = '', children, options, getLineId, ...props }) => {
	const [isHover, setIsHover] = useState(false)

	const [copied, setCopied] = useState(false)
	const [hasCopied, setHasCopied] = useState(false)

	const [isCollapsed, setCollapsed] = useState(true)
	let _language = (children?.props?.className || language).replace(/language-/, '')
	const languageToDisplay = `${_language}`

	if (_language && _language.toLowerCase() === 'json') {
		_language = 'javascript'
	}
	if (_language && _language.toLowerCase() === 'curl') {
		_language = ''
	}

	const sdkVersions = useAllSdkVersionsQuery()

	const trimmedCode = (children?.props?.children || code)?.trim()
	const _code = useMemo(() => {
		return sdkVersions.reduce((acc, sdk) => acc.replaceAll(`{{sdkVersion.${sdk.sdkId}}}`, sdk.version), trimmedCode)
	}, [trimmedCode])

	const lines = _code.split('\n').length

	const _options = { ...defaultOptions, ...options, ...children?.props }
	const { lineNumbers, copyToClipboard, highlight, showLanguageTag, collapse } = _options
	const _lineNumbers = JSON.parse(lineNumbers)

	const collapseNumber = parseInt(collapse)
	const shouldCollapse = Number.isFinite(collapseNumber)
	const isLineCollapsed = i => shouldCollapse && isCollapsed && collapseNumber < i + 1

	const arrayOfHighlightedLines = rangeParser(highlight || '')

	const shouldHighlightLine = lineNumber => {
		if (!arrayOfHighlightedLines?.length) return false
		return arrayOfHighlightedLines.includes(lineNumber)
	}

	const isLineFaded = i => arrayOfHighlightedLines.length && !shouldHighlightLine(i + 1)
	const isBackgroundHighlighted = i => shouldHighlightLine(i + 1)

	const onCopyClick = () => {
		setCopied(true)
		setHasCopied(true)

		copy(_code)
		const timer = setTimeout(() => {
			setCopied(false)
			clearTimeout(timer)
		}, 2000)

		const timer2 = setTimeout(() => {
			setHasCopied(false)
			clearTimeout(timer2)
		}, 2100)
	}

	const toggleCollapsed = () => setCollapsed(x => !x)

	const scrollAreaRef = useRef()
	const [scrollAreaWidth, setScrollAreaWidth] = useState('auto')
	useEffect(() => {
		setScrollAreaWidth(scrollAreaRef.current.offsetWidth + scrollAreaRef.current.offsetLeft)
	}, [scrollAreaRef.current])

	return (
		<Wrapper {...props} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
			<Highlight {...defaultProps} code={_code} language={_language} theme={null}>
				{({ className, style, tokens, getLineProps, getTokenProps }) => (
					<>
						<Outer data-component='Outer' showLanguageTag={showLanguageTag} className={className} style={style}>
							<CodeHolder data-component='CodeHolder'>
								<LinesBackground data-component='LineBackground' style={{ width: `100%`, minWidth: scrollAreaWidth }}>
									{tokens.map((x, i) =>
										isLineCollapsed(i) ? null : (
											<span style={{ display: 'block' }} key={i} data-highlight={isBackgroundHighlighted(i)} />
										),
									)}
								</LinesBackground>
								<LineNumbers data-component='LineNumbers'>
									{_lineNumbers &&
										tokens.map((_, i) =>
											isLineCollapsed(i) ? null : (
												<LineNumber key={i} faded={!!isLineFaded(i)} highlightBackground={isBackgroundHighlighted(i)}>
													{i + 1}
												</LineNumber>
											),
										)}
								</LineNumbers>
								<ScrollArea
									ref={scrollAreaRef}
									data-component='ScrollArea'
									lineNumbers={_lineNumbers}
									totalLines={lines}
								>
									{tokens.map((line, i) => {
										// Fix empty lines not being highlighted
										if (line.length === 1 && line[0].content === '') {
											line[0].content = ' '
										}

										return isLineCollapsed(i) ? null : (
											<Line
												id={getLineId?.(i) || null}
												key={i}
												{...getLineProps({ line, key: i })}
												faded={!!isLineFaded(i)}
												number={i + 1}
												lineNumbers={_lineNumbers}
											>
												{line.map((token, key) => (
													<span key={key} {...getTokenProps({ token, key })} />
												))}
											</Line>
										)
									})}
								</ScrollArea>
							</CodeHolder>
						</Outer>
						{shouldCollapse && (
							<CollapseButton onClick={toggleCollapsed}>
								{isCollapsed ? `Show all ${tokens.length} lines ↓` : 'Collapse ↑'}
							</CollapseButton>
						)}
					</>
				)}
			</Highlight>
			{!!showLanguageTag && !!languageToDisplay && (
				<LanguageTag lineNumbers={_lineNumbers}>{languageToDisplay}</LanguageTag>
			)}
			{!!copyToClipboard && (
				<CopyButton onClick={onCopyClick} data-copied={copied} visible={isHover || copied}>
					{hasCopied ? 'copied ✓' : 'copy'}
				</CopyButton>
			)}
		</Wrapper>
	)
}

CodeBlock.propTypes = {
	language: PropTypes.string,
	code: PropTypes.string,
	options: PropTypes.object,
	getLineId: PropTypes.func,
}

export default CodeBlock
